import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MatDialogRef,
} from '@angular/material/dialog';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { InputField } from '../../../../shared/components/input-field/input-field';
import { map, shareReplay } from 'rxjs';
import { ServersService } from '../../../../core/services/servers.service';
import { SyncDnsServerDialogResult } from '../../models/sync-dns-server.model';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-sync-dns-server-dialog',
  imports: [
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    FontAwesomeModule,
    InputField,
  ],
  templateUrl: './sync-dns-server-dialog.component.html',
  styleUrl: './sync-dns-server-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SyncDnsServerDialogComponent {
  private readonly dialogRef =
    inject<MatDialogRef<SyncDnsServerDialogComponent, SyncDnsServerDialogResult>>(MatDialogRef);

  private readonly serversService = inject(ServersService);

  readonly form = new FormGroup({
    server: new FormControl('', {
      nonNullable: true,
      validators: Validators.required,
    }),
    step: new FormControl<number | null>(null, {
      validators: [Validators.required, Validators.min(0), Validators.pattern(/^\d+$/)],
    }),
  });

  readonly servers$ = this.serversService.getAllServers().pipe(
    map((servers) =>
      servers.map((server) => ({
        label: server.hostname,
        value: server._id,
      })),
    ),
    shareReplay(1),
  );

  readonly serverOptions = toSignal(this.servers$, {
    initialValue: [],
  });

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const { server, step } = this.form.getRawValue();

    const serverHostname =
      this.serverOptions().find((option) => option.value === server)?.label ?? '';

    const result: SyncDnsServerDialogResult = {
      server,
      serverHostname,
      step: Number(step),
    };

    this.dialogRef.close(result);
  }

  close(): void {
    this.dialogRef.close();
  }
}
